import { useState } from "react";

import { StylesConfig } from "react-select";
import AsyncSelect from "react-select/async";

import { useReactSelectTheme } from "@bagel-web/components";

export type SelectOption = { label: string; value: string };

// A body-portaled menu needs a z-index above Bootstrap's overlays to stay
// visible.
const portalStyles: StylesConfig<SelectOption> = {
  menuPortal: (baseStyles) => ({
    ...baseStyles,
    zIndex: 1056,
  }),
};

// The service pickers' shared look: service names read as code.
export const monospaceSelectStyles: StylesConfig<SelectOption> = {
  container: (baseStyles) => ({
    ...baseStyles,
    minWidth: "300px",
  }),
  option: (baseStyles, { isSelected }) => ({
    ...baseStyles,
    fontFamily: "monospace",
    fontSize: ".875em",
    color: isSelected ? "white" : "var(--bs-code-color)",
  }),
  singleValue: (baseStyles) => ({
    ...baseStyles,
    fontFamily: "monospace",
    fontSize: ".875em",
    color: "var(--bs-code-color)",
  }),
  multiValueLabel: (baseStyles) => ({
    ...baseStyles,
    fontFamily: "monospace",
    fontSize: ".875em",
    color: "var(--bs-code-color)",
  }),
};

export type AsyncOptionsSelectProps = {
  // The selected option's value, resolved against the loaded options.
  value: string | null;
  // Resolve the options; `inputValue` carries the typed text for
  // searchable selects.
  fetchOptions: (inputValue: string) => Promise<Array<SelectOption>>;
  onChange: (e: { target: { value: string } }) => void;
  placeholder?: string;
  inputId?: string;
  disabled?: boolean;
  isClearable?: boolean;
  isSearchable?: boolean;
  // Render the menu at the document root so it overlays whatever contains
  // the select (e.g. a Card) instead of scrolling inside it.
  menuPortalToBody?: boolean;
  styles?: StylesConfig<SelectOption>;
};

// TODO(WEB-260 follow-up): migrate the remaining hand-rolled copies of this
// skeleton — admin's ServiceSelect and ServiceAliasSelect, and the
// dashboard's ServiceAliasSelect — onto this component.
/**
 * The async single-select skeleton shared by the option pickers: load
 * options through `fetchOptions`, resolve the selected value against them,
 * and render a themed react-select. Callers own the data fetching (and any
 * query caching) and map their records to options.
 */
const AsyncOptionsSelect = ({
  value,
  fetchOptions,
  onChange,
  placeholder,
  inputId,
  disabled = false,
  isClearable = false,
  isSearchable = false,
  menuPortalToBody = false,
  styles,
}: AsyncOptionsSelectProps) => {
  const [cachedOptions, setCachedOptions] = useState<Array<SelectOption>>([]);

  const loadOptions = (
    inputValue: string,
    callback: (options: Array<SelectOption>) => void
  ) => {
    fetchOptions(inputValue).then((options) => {
      setCachedOptions(options);
      callback(options);
    });
  };

  return (
    <AsyncSelect
      inputId={inputId}
      isDisabled={disabled}
      theme={useReactSelectTheme()}
      value={cachedOptions.find((option) => option.value === value) ?? null}
      cacheOptions={false}
      isSearchable={isSearchable}
      isClearable={isClearable}
      defaultOptions
      loadOptions={loadOptions}
      placeholder={placeholder || "Select..."}
      menuPortalTarget={
        menuPortalToBody && typeof document !== "undefined"
          ? document.body
          : null
      }
      styles={menuPortalToBody ? { ...portalStyles, ...styles } : styles}
      onChange={(newValue) => {
        // A cleared selection reports null; the change contract carries "".
        const selected = (newValue as { value: string } | null)?.value ?? "";
        onChange({ target: { value: selected } });
      }}
    />
  );
};

export default AsyncOptionsSelect;
